/**
 * Twitter/X API Integration
 * 
 * Handles Twitter API v2 for fetching tweets and user data.
 * Supports read-only operations with Bearer Token authentication.
 */

import { SocialMediaService, SocialPost, SocialAPIResponse, FeedParams, SocialPlatform, SocialMediaItem } from '../../types/social';

/**
 * Twitter API v2 response interfaces
 */
interface TwitterTweet {
  id: string;
  text: string;
  created_at: string;
  author_id: string;
  conversation_id: string;
  reply_settings: string;
  possibly_sensitive?: boolean;
  lang?: string;
  source?: string;
  context_annotations?: Array<{
    domain: {
      id: string;
      name: string;
      description: string;
    };
    entity: {
      id: string;
      name: string;
      description: string;
    };
  }>;
  entities?: {
    hashtags?: Array<{
      start: number;
      end: number;
      tag: string;
    }>;
    mentions?: Array<{
      start: number;
      end: number;
      username: string;
      id: string;
    }>;
    urls?: Array<{
      start: number;
      end: number;
      url: string;
      expanded_url: string;
      display_url: string;
      images?: Array<{
        url: string;
        width: number;
        height: number;
      }>;
    }>;
  };
  attachments?: {
    media_keys?: string[];
  };
  public_metrics: {
    retweet_count: number;
    like_count: number;
    reply_count: number;
    quote_count: number;
    bookmark_count?: number;
    impression_count?: number;
  };
  referenced_tweets?: Array<{
    type: 'retweeted' | 'quoted' | 'replied_to';
    id: string;
  }>;
}

interface TwitterUser {
  id: string;
  name: string;
  username: string;
  description?: string;
  profile_image_url?: string;
  verified?: boolean;
  verified_type?: string;
  public_metrics?: {
    followers_count: number;
    following_count: number;
    tweet_count: number;
    listed_count: number;
  };
}

interface TwitterMedia {
  media_key: string;
  type: 'photo' | 'video' | 'animated_gif';
  url?: string;
  preview_image_url?: string;
  width?: number;
  height?: number;
  duration_ms?: number;
  alt_text?: string;
}

interface TwitterAPIResponse {
  data: TwitterTweet[];
  includes?: {
    users?: TwitterUser[];
    media?: TwitterMedia[];
  };
  meta: {
    result_count: number;
    next_token?: string;
    previous_token?: string;
  };
}

/**
 * Twitter API Service
 */
export class TwitterService implements SocialMediaService {
  platform: SocialPlatform = 'twitter';
  private bearerToken: string;
  private baseURL = 'https://api.twitter.com/2';

  constructor(config: {
    bearerToken: string;
  }) {
    this.bearerToken = config.bearerToken;
  }

  /**
   * Transform Twitter tweet to unified format
   */
  private transformTweet(
    tweet: TwitterTweet, 
    users: TwitterUser[] = [], 
    media: TwitterMedia[] = []
  ): SocialPost {
    const author = users.find(user => user.id === tweet.author_id);
    const tweetMedia = this.extractMedia(tweet, media);
    const hashtags = tweet.entities?.hashtags?.map(h => h.tag) || [];
    const mentions = tweet.entities?.mentions?.map(m => m.username) || [];

    return {
      id: tweet.id,
      platform: 'twitter',
      content: tweet.text,
      author: {
        id: tweet.author_id,
        username: author?.username || 'unknown',
        displayName: author?.name || 'Unknown User',
        avatar: author?.profile_image_url?.replace('_normal', '_400x400'),
        verified: author?.verified || false
      },
      media: tweetMedia,
      metrics: {
        likes: tweet.public_metrics.like_count,
        comments: tweet.public_metrics.reply_count,
        shares: tweet.public_metrics.retweet_count + tweet.public_metrics.quote_count,
        views: tweet.public_metrics.impression_count || 0,
        reactions: {
          retweets: tweet.public_metrics.retweet_count,
          quotes: tweet.public_metrics.quote_count,
          bookmarks: tweet.public_metrics.bookmark_count || 0
        }
      },
      timestamps: {
        created: new Date(tweet.created_at)
      },
      url: `https://twitter.com/${author?.username || 'i'}/status/${tweet.id}`,
      type: this.mapTweetType(tweet, tweetMedia),
      visibility: 'public',
      platformData: {
        conversationId: tweet.conversation_id,
        replySettings: tweet.reply_settings,
        lang: tweet.lang,
        source: tweet.source,
        contextAnnotations: tweet.context_annotations,
        referencedTweets: tweet.referenced_tweets
      },
      hashtags,
      mentions
    };
  }

  /**
   * Extract media from Twitter tweet
   */
  private extractMedia(tweet: TwitterTweet, allMedia: TwitterMedia[]): SocialMediaItem[] {
    if (!tweet.attachments?.media_keys) {
      return [];
    }

    return tweet.attachments.media_keys
      .map(key => allMedia.find(m => m.media_key === key))
      .filter(Boolean)
      .map(media => ({
        id: media!.media_key,
        type: media!.type === 'animated_gif' ? 'gif' : media!.type === 'photo' ? 'image' : 'video',
        url: media!.url || media!.preview_image_url || '',
        thumbnail: media!.preview_image_url,
        dimensions: media!.width && media!.height ? {
          width: media!.width,
          height: media!.height
        } : undefined,
        duration: media!.duration_ms ? media!.duration_ms / 1000 : undefined,
        altText: media!.alt_text
      }));
  }

  /**
   * Map tweet type based on content and media
   */
  private mapTweetType(tweet: TwitterTweet, media: SocialMediaItem[]): SocialPost['type'] {
    if (media.length === 0) return 'text';
    if (media.length > 1) return 'carousel';
    if (media[0].type === 'video' || media[0].type === 'gif') return 'video';
    return 'image';
  }

  /**
   * Fetch user tweets
   */
  async getUserPosts(params: FeedParams): Promise<SocialAPIResponse<SocialPost[]>> {
    try {
      const username = params.username;
      if (!username) {
        throw new Error('Username required for Twitter posts');
      }

      // First, get user ID from username
      const userResponse = await fetch(
        `${this.baseURL}/users/by/username/${username}?user.fields=id,name,username,profile_image_url,verified,verified_type,public_metrics`,
        {
          headers: {
            'Authorization': `Bearer ${this.bearerToken}`
          }
        }
      );

      if (!userResponse.ok) {
        throw new Error(`Twitter API error: ${userResponse.status} ${userResponse.statusText}`);
      }

      const userData = await userResponse.json();
      if (userData.errors) {
        throw new Error(`Twitter API error: ${userData.errors[0].detail}`);
      }

      const userId = userData.data.id;

      // Build query parameters
      const queryParams = new URLSearchParams({
        'tweet.fields': [
          'created_at',
          'author_id',
          'conversation_id',
          'reply_settings',
          'public_metrics',
          'entities',
          'attachments',
          'referenced_tweets',
          'lang',
          'source',
          'context_annotations'
        ].join(','),
        'user.fields': 'id,name,username,profile_image_url,verified,verified_type',
        'media.fields': 'media_key,type,url,preview_image_url,width,height,duration_ms,alt_text',
        'expansions': 'author_id,attachments.media_keys',
        'max_results': Math.min(params.limit || 10, 100).toString()
      });

      if (params.nextToken) {
        queryParams.append('pagination_token', params.nextToken);
      }

      if (!params.includeRetweets) {
        queryParams.append('exclude', 'retweets');
      }

      if (!params.includeReplies) {
        queryParams.append('exclude', 'replies');
      }

      // Fetch tweets
      const response = await fetch(
        `${this.baseURL}/users/${userId}/tweets?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${this.bearerToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Twitter API error: ${response.status} ${response.statusText}`);
      }

      const data: TwitterAPIResponse = await response.json();

      if (data.data?.length === 0) {
        return {
          data: [],
          platform: 'twitter',
          meta: {
            total: 0,
            rateLimit: this.parseRateLimit(response.headers)
          },
          timestamp: new Date()
        };
      }

      const posts = data.data.map(tweet => 
        this.transformTweet(tweet, data.includes?.users, data.includes?.media)
      );

      return {
        data: posts,
        platform: 'twitter',
        meta: {
          total: data.meta.result_count,
          nextToken: data.meta.next_token,
          rateLimit: this.parseRateLimit(response.headers)
        },
        timestamp: new Date()
      };

    } catch (error) {
      console.error('Failed to fetch Twitter posts:', error);
      throw error;
    }
  }

  /**
   * Parse rate limit from response headers
   */
  private parseRateLimit(headers: Headers): { remaining: number; reset: Date } {
    const remaining = parseInt(headers.get('x-rate-limit-remaining') || '0');
    const reset = parseInt(headers.get('x-rate-limit-reset') || '0');
    
    return {
      remaining,
      reset: new Date(reset * 1000)
    };
  }

  /**
   * Fetch single tweet
   */
  async getPost(postId: string): Promise<SocialAPIResponse<SocialPost>> {
    try {
      const queryParams = new URLSearchParams({
        'tweet.fields': [
          'created_at',
          'author_id',
          'conversation_id',
          'reply_settings',
          'public_metrics',
          'entities',
          'attachments',
          'referenced_tweets',
          'lang',
          'source'
        ].join(','),
        'user.fields': 'id,name,username,profile_image_url,verified,verified_type',
        'media.fields': 'media_key,type,url,preview_image_url,width,height,duration_ms,alt_text',
        'expansions': 'author_id,attachments.media_keys'
      });

      const response = await fetch(
        `${this.baseURL}/tweets/${postId}?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${this.bearerToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Twitter API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`Twitter API error: ${data.errors[0].detail}`);
      }

      const post = this.transformTweet(data.data, data.includes?.users, data.includes?.media);

      return {
        data: post,
        platform: this.platform,
        meta: {
          rateLimit: this.parseRateLimit(response.headers)
        },
        timestamp: new Date()
      };

    } catch (error) {
      console.error('Failed to fetch Twitter post:', error);
      throw error;
    }
  }

  /**
   * Fetch user profile
   */
  async getUserProfile(username: string): Promise<SocialAPIResponse<SocialPost['author']>> {
    try {
      const response = await fetch(
        `${this.baseURL}/users/by/username/${username}?user.fields=id,name,username,profile_image_url,verified,verified_type,public_metrics`,
        {
          headers: {
            'Authorization': `Bearer ${this.bearerToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Twitter API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`Twitter API error: ${data.errors[0].detail}`);
      }

      const user = data.data;
      const profile = {
        id: user.id,
        username: user.username,
        displayName: user.name,
        avatar: user.profile_image_url?.replace('_normal', '_400x400'),
        verified: user.verified || false
      };

      return {
        data: profile,
        platform: this.platform,
        meta: {
          rateLimit: this.parseRateLimit(response.headers)
        },
        timestamp: new Date()
      };

    } catch (error) {
      console.error('Failed to fetch Twitter profile:', error);
      throw error;
    }
  }

  /**
   * Validate credentials
   */
  async validateCredentials(): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseURL}/users/me`,
        {
          headers: {
            'Authorization': `Bearer ${this.bearerToken}`
          }
        }
      );

      return response.ok;

    } catch (error) {
      console.error('Failed to validate Twitter credentials:', error);
      return false;
    }
  }

  /**
   * Get rate limit status
   */
  async getRateLimit(): Promise<{ remaining: number; reset: Date }> {
    try {
      const response = await fetch(
        `${this.baseURL}/users/me`,
        {
          headers: {
            'Authorization': `Bearer ${this.bearerToken}`
          }
        }
      );

      return this.parseRateLimit(response.headers);

    } catch (error) {
      console.error('Failed to get Twitter rate limit:', error);
      return {
        remaining: 0,
        reset: new Date()
      };
    }
  }
}

/**
 * Mock Twitter service for demo purposes
 */
export class MockTwitterService implements SocialMediaService {
  platform: SocialPlatform = 'twitter';

  async getUserPosts(params: FeedParams): Promise<SocialAPIResponse<SocialPost[]>> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));

    const mockTweets: SocialPost[] = [
      {
        id: 'tw_1',
        platform: 'twitter',
        content: 'Building something amazing with React and TypeScript! 🚀 The developer experience is just incredible. #ReactJS #TypeScript #WebDev',
        author: {
          id: 'user_456',
          username: 'devlife',
          displayName: 'Developer Life',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
          verified: false
        },
        media: [
          {
            id: 'tw_img_1',
            type: 'image',
            url: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600',
            dimensions: { width: 600, height: 400 }
          }
        ],
        metrics: {
          likes: 245,
          comments: 42,
          shares: 67,
          views: 3400,
          reactions: {
            retweets: 52,
            quotes: 15,
            bookmarks: 89
          }
        },
        timestamps: {
          created: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
        },
        url: 'https://twitter.com/devlife/status/1',
        type: 'image',
        visibility: 'public',
        hashtags: ['ReactJS', 'TypeScript', 'WebDev'],
        mentions: []
      },
      {
        id: 'tw_2',
        platform: 'twitter',
        content: 'Hot take: Clean code is more important than clever code. Write code that your future self will thank you for. 💭 #CleanCode #Programming',
        author: {
          id: 'user_456',
          username: 'devlife',
          displayName: 'Developer Life',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
          verified: false
        },
        media: [],
        metrics: {
          likes: 1247,
          comments: 156,
          shares: 289,
          views: 12400,
          reactions: {
            retweets: 234,
            quotes: 55,
            bookmarks: 445
          }
        },
        timestamps: {
          created: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
        },
        url: 'https://twitter.com/devlife/status/2',
        type: 'text',
        visibility: 'public',
        hashtags: ['CleanCode', 'Programming'],
        mentions: []
      },
      {
        id: 'tw_3',
        platform: 'twitter',
        content: 'Just shipped a new feature! 🎉 Sometimes the best feeling is seeing your code work in production. What was your last "it works!" moment? 🤔',
        author: {
          id: 'user_456',
          username: 'devlife',
          displayName: 'Developer Life',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
          verified: false
        },
        media: [
          {
            id: 'tw_img_3',
            type: 'image',
            url: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600',
            dimensions: { width: 600, height: 400 }
          }
        ],
        metrics: {
          likes: 567,
          comments: 89,
          shares: 123,
          views: 4567,
          reactions: {
            retweets: 98,
            quotes: 25,
            bookmarks: 234
          }
        },
        timestamps: {
          created: new Date(Date.now() - 8 * 60 * 60 * 1000), // 8 hours ago
        },
        url: 'https://twitter.com/devlife/status/3',
        type: 'image',
        visibility: 'public',
        hashtags: [],
        mentions: []
      }
    ];

    return {
      data: mockTweets.slice(0, params.limit || 10),
      platform: 'twitter',
      meta: {
        total: mockTweets.length,
        page: 1,
        perPage: params.limit || 10,
        rateLimit: {
          remaining: 75,
          reset: new Date(Date.now() + 15 * 60 * 1000) // 15 minutes
        }
      },
      timestamp: new Date()
    };
  }

  async getPost(postId: string): Promise<SocialAPIResponse<SocialPost>> {
    const posts = await this.getUserPosts({ platform: 'twitter' });
    const post = posts.data.find(p => p.id === postId);
    
    if (!post) {
      throw new Error('Tweet not found');
    }

    return {
      data: post,
      platform: this.platform,
      meta: {
        rateLimit: {
          remaining: 74,
          reset: new Date(Date.now() + 15 * 60 * 1000)
        }
      },
      timestamp: new Date()
    };
  }

  async getUserProfile(username: string): Promise<SocialAPIResponse<SocialPost['author']>> {
    await new Promise(resolve => setTimeout(resolve, 400));

    return {
      data: {
        id: 'user_456',
        username: 'devlife',
        displayName: 'Developer Life',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
        verified: false
      },
      platform: this.platform,
      meta: {
        rateLimit: {
          remaining: 299,
          reset: new Date(Date.now() + 15 * 60 * 1000)
        }
      },
      timestamp: new Date()
    };
  }

  async validateCredentials(): Promise<boolean> {
    return true;
  }

  async getRateLimit(): Promise<{ remaining: number; reset: Date }> {
    return {
      remaining: 75,
      reset: new Date(Date.now() + 15 * 60 * 1000)
    };
  }
}